import React, { useState, useEffect } from "react";
import { getBanners } from "../services/bannerService.js"; 
import { 
  Plus, 
  Image as ImageIcon, 
  Edit2, 
  Trash2, 
  UploadCloud, 
  X, 
  Loader2,
  CheckCircle2,
  LayoutTemplate
} from "lucide-react";
import "./Banners.css";

export default function BannersDashboard() {
  const [banners, setBanners] = useState([]);
  const [siteId, setSiteId] = useState(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    titulo: "",
    descricao: "",
    botao_texto: "",
    botao_link: "",
    imagem: "",
    ordem: 1,
    ativo: true,
  });

  useEffect(() => {
    const idLocalStorage = localStorage.getItem("siteId");
    
    if (idLocalStorage && idLocalStorage !== "undefined") {
      setSiteId(idLocalStorage);
      carregarBanners(idLocalStorage);
    } else {
      console.error("ID do site não encontrado no LocalStorage");
      alert("Sessão inválida. Por favor, faça login novamente.");
    }
  }, []);

  const carregarBanners = async (id) => {
    try {
      const data = await getBanners(id);
      setBanners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao buscar banners:", error);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setArquivoSelecionado(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let imagemUrl = formData.imagem;

      if (arquivoSelecionado) {
        const s3Data = new FormData();
        s3Data.append("file", arquivoSelecionado);
        s3Data.append("cliente_id", siteId); 

        const resS3 = await fetch("/api/upload", {
          method: "POST",
          body: s3Data,
        });

        if (!resS3.ok) throw new Error("Erro no upload da imagem");
        const dataS3 = await resS3.json();
        imagemUrl = dataS3.url; 
      }

      const payload = {
        site_id: siteId,
        titulo: formData.titulo,
        descricao: formData.descricao,
        botao_texto: formData.botao_texto,
        botao_link: formData.botao_link,
        imagem: imagemUrl, 
        ordem: parseInt(formData.ordem),
        ativo: formData.ativo,
      };

      const urlBase = "/api/banners";
      const method = formData.id ? "PUT" : "POST";
      const url = formData.id ? `${urlBase}/${formData.id}` : urlBase;

      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Erro ao salvar no banco de dados");

      fecharModal();
      carregarBanners(siteId);

    } catch (error) {
      console.error("Erro ao salvar banner:", error);
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja remover este banner?")) return;

    try {
      await fetch(`/api/banners/${id}`, { method: "DELETE" });
      carregarBanners(siteId);
    } catch (error) {
      console.error("Erro ao deletar:", error);
    }
  };

  const abrirModalParaCriar = () => {
    setFormData({ id: null, titulo: "", descricao: "", botao_texto: "", botao_link: "", imagem: "", ordem: 1, ativo: true });
    setArquivoSelecionado(null);
    setIsModalOpen(true);
  };

  const abrirModalParaEditar = (banner) => {
    setFormData(banner);
    setArquivoSelecionado(null);
    setIsModalOpen(true);
  };

  const fecharModal = () => setIsModalOpen(false);

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        
        {/* Cabeçalho */}
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>Gestão de Banners</h1>
            <p>Gerencie os destaques exibidos na página inicial do portal.</p>
          </div>
          <button onClick={abrirModalParaCriar} className="btn btn-primary shadow-sm">
            <Plus size={18} /> Novo Banner
          </button>
        </div>

        {/* Grid de Banners ou Empty State */}
        {banners.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <LayoutTemplate size={48} strokeWidth={1.5} />
            </div>
            <h3>Nenhum banner cadastrado</h3>
            <p>Comece criando um novo banner para dar vida à sua página inicial.</p>
            <button onClick={abrirModalParaCriar} className="btn btn-primary mt-3">
              <Plus size={18} /> Criar Primeiro Banner
            </button>
          </div>
        ) : (
          <div className="banners-grid">
            {banners.map((banner) => (
              <div key={banner.id} className="banner-card">
                <div className="banner-image-container">
                  {banner.imagem ? (
                    <img src={banner.imagem} alt={banner.titulo} />
                  ) : (
                    <div className="no-image-placeholder">
                      <ImageIcon size={32} />
                      <span>Sem imagem</span>
                    </div>
                  )}
                  {banner.ativo && <span className="banner-status">Ativo</span>}
                </div>
                
                <div className="banner-content">
                  <h3 title={banner.titulo}>{banner.titulo}</h3>
                  <p title={banner.descricao}>{banner.descricao}</p>
                  
                  <div className="banner-actions">
                    <button onClick={() => abrirModalParaEditar(banner)} className="btn btn-secondary btn-sm">
                      <Edit2 size={16} /> Editar
                    </button>
                    <button onClick={() => handleDelete(banner.id)} className="btn btn-danger btn-sm">
                      <Trash2 size={16} /> Excluir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Elegante */}
        {isModalOpen && (
          <div className="modal-overlay" onClick={(e) => { if(e.target === e.currentTarget) fecharModal() }}>
            <div className="modal-content">
              
              <div className="modal-header">
                <h2>{formData.id ? "Editar Banner" : "Criar Novo Banner"}</h2>
                <button onClick={fecharModal} className="btn-close" title="Fechar">
                  <X size={20} />
                </button>
              </div>
              
              <div className="modal-body">
                <form id="bannerForm" onSubmit={handleSubmit}>
                  
                  <div className="form-group">
                    <label className="form-label">Imagem do Banner</label>
                    <label className="file-upload-box">
                      <UploadCloud size={32} className="upload-icon" />
                      <div className="file-upload-text">Clique para fazer upload da imagem</div>
                      <span className="file-upload-hint">PNG, JPG ou GIF (Max. 5MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleFileChange} 
                        required={!formData.id && !formData.imagem} 
                      />
                    </label>
                    
                    {arquivoSelecionado && (
                      <div className="file-info success">
                        <CheckCircle2 size={16} /> Arquivo selecionado: {arquivoSelecionado.name}
                      </div>
                    )}
                    
                    {formData.imagem && !arquivoSelecionado && (
                      <div className="file-info neutral">
                        <ImageIcon size={16} /> Imagem atual mantida. Selecione outra para substituir.
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Título</label>
                    <input 
                      type="text" 
                      className="form-input"
                      value={formData.titulo} 
                      onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                      placeholder="Ex: Campanha de Educação Previdenciária"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Descrição Breve</label>
                    <textarea 
                      className="form-textarea"
                      value={formData.descricao} 
                      onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                      placeholder="Um texto chamativo que aparecerá embaixo do título..."
                    />
                  </div>

                  <div className="form-group grid-2">
                    <div>
                      <label className="form-label">Texto do Botão</label>
                      <input 
                        type="text" 
                        className="form-input"
                        value={formData.botao_texto} 
                        onChange={(e) => setFormData({...formData, botao_texto: e.target.value})}
                        placeholder="Ex: Saiba Mais"
                      />
                    </div>
                    <div>
                      <label className="form-label">Link do Botão</label>
                      <input 
                        type="text" 
                        className="form-input"
                        value={formData.botao_link} 
                        onChange={(e) => setFormData({...formData, botao_link: e.target.value})}
                        placeholder="Ex: /institucional"
                      />
                    </div>
                  </div>
                </form>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={fecharModal} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" form="bannerForm" disabled={isLoading} className="btn btn-primary">
                  {isLoading ? (
                    <><Loader2 size={18} className="spinner" /> Salvando...</>
                  ) : (
                    "Salvar Banner"
                  )}
                </button>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}